import { useEffect, useState } from 'react';

import { faTimes } from '@fortawesome/pro-light-svg-icons';
import Decimal from 'decimal.js';
import { useTranslation } from 'react-i18next';

import {
  OrganizationalUnit,
  PlanUnit,
  SharedOrganizationalUnitExt
} from 'components/types';

import { displayNumber, toDecimalPlaces } from 'utils/misc';

import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { FontAwesomeSvgIcon, VirtualizedAutocomplete } from 'components/shared';
import { Typography } from '@mui/material';
import { ORANGE_SECONDARY } from 'constants/colors';

type UpdateProps =
  | {
      organizationalUnit: OrganizationalUnit;
    }
  | {
      hours?: number | null;
      percentage: number;
    };

type Props = {
  unit: SharedOrganizationalUnitExt;
  isEditMode: boolean;
  diffHours: number | null;
  diffPercentage: number;
  employmentHours?: number;
  planUnits: PlanUnit[];
  onUpdate: (props: UpdateProps) => void;
  onDelete: () => void;
};

export default function SharedOrganizationalUnit(props: Props) {
  const { t } = useTranslation('common');
  const {
    unit,
    isEditMode,
    diffHours,
    diffPercentage,
    employmentHours,
    planUnits,
    onUpdate,
    onDelete
  } = props;
  const { organizationalUnit, hours, percentage, isBlocked, isNew } = unit;
  const [showHoursError, setShowHoursError] = useState(false);
  const [showPercentageError, setShowPercentageError] = useState(false);

  const maxHours =
    hours === null
      ? diffHours || 0
      : Decimal.add(hours || 0, diffHours || 0).toNumber();
  const maxPercentage =
    percentage === null
      ? diffPercentage
      : Decimal.mul(percentage, 100).add(diffPercentage).toNumber();

  useEffect(() => {
    if (!isEditMode) {
      setShowHoursError(false);
      setShowPercentageError(false);
    }
  }, [isEditMode]);

  return (
    <TableRow className="shared-organizational-unit">
      <TableCell className="planunit-cell">
        {!isNew && isBlocked && (
          <Tooltip title={t('createdByPeriodAssignment')}>
            <Typography
              fontSize={13}
              sx={{
                color: ORANGE_SECONDARY + '!important',
                fontStyle: 'italic'
              }}
            >
              {organizationalUnit?.name}
            </Typography>
          </Tooltip>
        )}
        {!isNew && !isBlocked && (
          <Typography
            fontSize={13}
            sx={{ fontFamily: 'signikaregular!important' }}
          >
            {organizationalUnit?.name}
          </Typography>
        )}
        {isNew && (
          <VirtualizedAutocomplete
            options={planUnits}
            getOptionLabel={(o) => o.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('organizationalUnit')}
                size="small"
              />
            )}
            onChange={(e, { id, name }) => {
              onUpdate({ organizationalUnit: { id, name } });
            }}
            autoHighlight
            disableClearable
            multiple={false}
            freeSolo={false}
          />
        )}
      </TableCell>
      {employmentHours && (
        <TableCell className="hours-cell">
          <Tooltip
            arrow
            title={
              t('planUnitDistribution.maxValueErrorMsg', {
                max: displayNumber(maxHours)
              }) || ''
            }
            open={showHoursError}
          >
            <TextField
              size="small"
              value={hours != null ? toDecimalPlaces(hours) : ''}
              type="number"
              inputProps={{ min: 0, max: maxHours }}
              disabled={!isEditMode}
              onChange={(e) => {
                const value = parseFloat(e.target.value.replace(/-/g, ''));
                if (value > maxHours) {
                  setShowHoursError(true);
                } else {
                  const hours = Number.isNaN(value) ? null : value;
                  onUpdate({
                    hours,
                    percentage:
                      hours == null
                        ? 0
                        : Decimal.div(hours, employmentHours).toNumber()
                  });
                  if (showHoursError) {
                    setShowHoursError(false);
                  }
                }
              }}
              fullWidth
              error={showHoursError}
            />
          </Tooltip>
        </TableCell>
      )}
      <TableCell className="percentage-cell">
        <Tooltip
          arrow
          title={
            t('planUnitDistribution.maxValueErrorMsg', {
              max: displayNumber(maxPercentage)
            }) || ''
          }
          open={showPercentageError}
        >
          <TextField
            size="small"
            value={toDecimalPlaces(Decimal.mul(percentage, 100).toNumber())}
            type="number"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              min: 0,
              max: maxPercentage
            }}
            disabled={!isEditMode}
            onChange={(e) => {
              const value = parseFloat(e.target.value.replace(/-/g, ''));
              if (value > maxPercentage) {
                setShowPercentageError(true);
              } else {
                onUpdate({
                  percentage: Decimal.div(value, 100).toNumber(),
                  hours: employmentHours
                    ? Decimal.div(value, 100).mul(employmentHours).toNumber()
                    : undefined
                });
                if (showPercentageError) {
                  setShowPercentageError(false);
                }
              }
            }}
            fullWidth
            error={showPercentageError}
          />
        </Tooltip>
      </TableCell>
      <TableCell className="actions-cell">
        {isEditMode && (
          <div className="actions d-flex v-center">
            <IconButton onClick={() => onDelete()} size="small">
              <FontAwesomeSvgIcon icon={faTimes} fontSize="small" />
            </IconButton>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
